import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';

import {
  CurrencyValue,
  Flex,
  LineChart,
  formatCurrency,
  formatDatetime,
  formatNumber
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';

export default function HistoricTabs() {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');

  const [data, error] = useApi({
    url: '/buffer/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod({
    options: {
      scales: {
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            // Optional: Customize tick labels if needed (e.g., format as currency)
            callback: function (value) {
              return Number(value) >= 0 ? formatCurrency(Number(value)) : ''; // Explicitly skip any negative labels (though min:0 already prevents this)
            }
          }
        }
      }
    }
  });

  const yScaleValueFormatter = (value) => {
    return `${formatCurrency(value)}`;
  };

  const valueFormatter = (value) => {
    function formatValue(value) {
      return (
        <CurrencyValue
          value={Number(value)}
          options={{
            currency: 'USD',
            iconSize: '3'
          }}
        />
      );
    }

    return formatValue(value);
  };

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <LineChart
          key="surplus"
          data={data}
          datasets={[
            {
              xField: 'date',
              yField: 'sky_surplus',
              label: '**Allocated Genesis Capital',
              borderColor: 'rgba(228, 188, 56)',
              backgroundColor: 'rgba(228, 188, 56, 0.8)'
            },
            {
              xField: 'date',
              yField: 'spark_proxy_balance',
              label: 'Spark',
              borderColor: 'rgba(250, 67, 189, 1)',
              backgroundColor: 'rgba(250, 67, 189, 0.8)'
            },
            {
              xField: 'date',
              yField: 'grove_proxy_balance',
              label: 'Grove',
              borderColor: 'rgba(39, 146, 57, 1)',
              backgroundColor: 'rgba(39, 146, 57, 0.8)'
            },
            {
              xField: 'date',
              yField: 'obex_proxy_balance',
              label: 'Obex',
              borderColor: 'rgba(0, 51, 102, 1)',
              backgroundColor: 'rgba(0, 51, 102, 0.8)'
            }
          ]}
          yScaleValueFormatter={yScaleValueFormatter}
          valueFormatter={valueFormatter}
          timeFilter={timeFilter}
          config={config}
          showKpisAsTooltip={true}
          tooltip={{
            labelFormatter: (value) => `${value}`,
            titleFormatter: (_value, tooltipItems) => {
              const dateValue =
                tooltipItems[0]?.raw &&
                typeof tooltipItems[0].raw === 'object' &&
                'x' in tooltipItems[0].raw
                  ? (tooltipItems[0].raw as { x?: string | number })?.x
                  : tooltipItems[0]?.parsed &&
                      typeof tooltipItems[0].parsed === 'object' &&
                      'x' in tooltipItems[0].parsed
                    ? (tooltipItems[0].parsed as { x?: string | number })?.x
                    : undefined;
              return `Date: ${formatDatetime(dateValue, { format: 'MMM dd, yyyy' })}`;
            },
            valueFormatter: (value) =>
              `$${formatNumber(Math.abs(Number(value)))}`
          }}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
